import React, { useEffect, useState, useCallback } from "react";
import {
  Spin, Alert, Table, Tag, Button, Drawer, Form, Input, Select,
  Checkbox, message, Popconfirm, Switch, Space,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import styled from "styled-components";

const PageWrapper = styled.div`
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;
const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 28px; flex-wrap: wrap; gap: 16px;
`;
const PageTitle = styled.h1`
  font-size: 26px; font-weight: 600; color: #1e293b; letter-spacing: -0.5px; margin: 0;
`;
const PageSubtitle = styled.p`font-size: 14px; color: #64748b; margin: 4px 0 0;`;
const TableCard = styled.div`
  background: white; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0; overflow: hidden;
`;
const FeatureGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
`;

const FEATURES = [
  { key: "overview", label: "Overview" },
  { key: "gmv", label: "GMV" },
  { key: "revenue", label: "Revenue" },
  { key: "funnel", label: "Creator Funnel" },
  { key: "brand-pages", label: "Brand Pages" },
  { key: "users", label: "Users" },
  { key: "settlements", label: "Settlement Stats" },
  { key: "customers", label: "Customers" },
  { key: "transactions", label: "Transactions" },
  { key: "leads", label: "Leads" },
  { key: "new-registrations", label: "New Registrations" },
  { key: "saas-plans", label: "SaaS Plans" },
  { key: "insta-plans", label: "Insta Auto Plans" },
  { key: "settlement-ops", label: "Settlement Operations" },
  { key: "seo-blogs", label: "SEO Blogs" },
];

const ROLE_COLORS = { superadmin: "purple", admin: "blue" };

function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get("/admin-auth/users");
      setUsers(res.data.data?.users || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "admin", allowedFeatures: ["overview"] });
    setDrawerOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      name: user.name,
      role: user.role,
      allowedFeatures: user.allowedFeatures || [],
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingUser) {
        await customClientAxios().patch(`/admin-auth/users/${editingUser._id}`, {
          name: values.name,
          role: values.role,
          allowedFeatures: values.allowedFeatures || [],
        });
        message.success("Admin user updated");
      } else {
        await customClientAxios().post("/admin-auth/users", {
          email: values.email,
          name: values.name,
          role: values.role,
          allowedFeatures: values.allowedFeatures || [],
        });
        message.success("Admin user created");
      }
      setDrawerOpen(false);
      form.resetFields();
      fetchUsers();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await customClientAxios().patch(`/admin-auth/users/${user._id}`, {
        isActive: !user.isActive,
      });
      message.success(user.isActive ? "Deactivated" : "Activated");
      fetchUsers();
    } catch (e) {
      message.error("Failed to update");
    }
  };

  const handleDelete = async (user) => {
    try {
      await customClientAxios().delete(`/admin-auth/users/${user._id}`);
      message.success("Deleted");
      fetchUsers();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to delete");
    }
  };

  const roleValue = Form.useWatch("role", form);

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {r.photoURL ? (
            <img src={r.photoURL} alt="" style={{ width: 32, height: 32, borderRadius: 8 }} referrerPolicy="no-referrer" />
          ) : (
            <div style={{
              width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white", fontWeight: 600, fontSize: 13,
            }}>
              {(r.name || r.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name || "—"}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 110,
      render: (v) => <Tag color={ROLE_COLORS[v]}>{v}</Tag>,
    },
    {
      title: "Features",
      key: "features",
      render: (_, r) => r.role === "superadmin"
        ? <Tag color="purple">All Features</Tag>
        : (r.allowedFeatures || []).length > 0
          ? <span style={{ fontSize: 12, color: "#64748b" }}>{r.allowedFeatures.length} features</span>
          : <span style={{ fontSize: 12, color: "#ef4444" }}>None</span>,
    },
    {
      title: "Active",
      key: "isActive",
      width: 80,
      render: (_, r) => (
        <Switch size="small" checked={r.isActive} onChange={() => handleToggleActive(r)} />
      ),
    },
    {
      title: "Last Login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Never",
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete this admin?" onConfirm={() => handleDelete(r)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageWrapper>
      <PageHeader>
        <div>
          <PageTitle>Admin Users</PageTitle>
          <PageSubtitle>Manage who can access the admin panel and what they can see.</PageSubtitle>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Admin
        </Button>
      </PageHeader>

      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <TableCard>
        <Spin spinning={loading}>
          <Table
            dataSource={users}
            columns={columns}
            rowKey="_id"
            size="middle"
            pagination={false}
          />
        </Spin>
      </TableCard>

      <Drawer
        title={editingUser ? "Edit Admin User" : "Add Admin User"}
        placement="right"
        width={440}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ padding: "12px 0" }}>
            <Button type="primary" loading={submitting} block onClick={() => form.submit()}>
              {editingUser ? "Save Changes" : "Create Admin"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: "email", message: "Valid email required" }]}
          >
            <Input placeholder="admin@example.com" disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "superadmin", label: "Super Admin — full access to everything" },
                { value: "admin", label: "Admin — access to selected features only" },
              ]}
            />
          </Form.Item>

          {roleValue !== "superadmin" && (
            <Form.Item name="allowedFeatures" label="Allowed Features">
              <Checkbox.Group style={{ width: "100%" }}>
                <FeatureGrid>
                  {FEATURES.map((f) => (
                    <Checkbox key={f.key} value={f.key} style={{ fontSize: 13 }}>
                      {f.label}
                    </Checkbox>
                  ))}
                </FeatureGrid>
              </Checkbox.Group>
            </Form.Item>
          )}

          {roleValue === "superadmin" && (
            <Alert
              type="info"
              message="Super admins have access to all features automatically."
              style={{ marginBottom: 16 }}
            />
          )}
        </Form>
      </Drawer>
    </PageWrapper>
  );
}

export default AdminUsersPage;
